import { useCallback, useRef, useState } from 'react'
import { AppUseSelector } from '../redux/hooks'
import { getSignedUrl } from '../utils/S3Upload'

// Audio file constants
const FEMALE_VOICE_USER_ID = '6605279e1d5babe34ff11035'
const MALE_VOICE_USER_ID = '675a2b3c4d5e6f7890123456'

export type AudioSetting = 'both' | 'female_only' | 'male_only' | 'none'

interface WordAudioData {
  _id: string
  has_audio?: boolean
}

export const getAudioKey = (wordId: string, userId: string): string => {
  return `audio/pronunciations/${userId}/${wordId}.mp3`
}

export const hasAudioRecording = (has_audio: boolean | undefined): boolean => {
  return has_audio === true
}

export const useWordAudio = () => {
  const { audio_pronunciation_setting } = AppUseSelector(({ story }) => story)
  const [isPlaying, setIsPlaying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const sessionIdRef = useRef(0)

  const stopCurrentAudio = useCallback(() => {
    sessionIdRef.current++
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
    setIsPlaying(false)
  }, [])

  const playAudio = useCallback((url: string, sessionId: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (sessionId !== sessionIdRef.current) {
        resolve()
        return
      }

      const audio = new Audio(url)
      audioRef.current = audio

      audio.onended = () => {
        resolve()
      }
      audio.onerror = () => {
        reject(new Error('Audio playback failed'))
      }
      audio.oncanplaythrough = () => {
        if (sessionId === sessionIdRef.current) {
          audio.play().catch(reject)
        } else {
          audio.pause()
          resolve()
        }
      }
    })
  }, [])

  const playWordAudio = useCallback(
    async (wordData: WordAudioData, overrideSetting?: AudioSetting) => {
      const setting = overrideSetting || audio_pronunciation_setting

      // Check if word has audio recording
      if (!hasAudioRecording(wordData.has_audio)) {
        return
      }

      // Don't play if setting is 'none' and no override
      if (setting === 'none' && !overrideSetting) {
        return
      }

      // Stop any currently playing audio and get new session ID
      stopCurrentAudio()
      const currentSessionId = sessionIdRef.current

      setIsPlaying(true)
      setError(null)

      try {
        const maleKey = getAudioKey(wordData._id, MALE_VOICE_USER_ID)
        const femaleKey = getAudioKey(wordData._id, FEMALE_VOICE_USER_ID)

        if (setting === 'male_only') {
          const signedUrl = await getSignedUrl({ key: maleKey })
          await playAudio(signedUrl, currentSessionId)
        } else if (setting === 'female_only') {
          const signedUrl = await getSignedUrl({ key: femaleKey })
          await playAudio(signedUrl, currentSessionId)
        } else {
          // 'both' - play male first, then female
          const maleSignedUrl = await getSignedUrl({ key: maleKey })
          await playAudio(maleSignedUrl, currentSessionId)
          if (currentSessionId === sessionIdRef.current) {
            const femaleSignedUrl = await getSignedUrl({ key: femaleKey })
            await playAudio(femaleSignedUrl, currentSessionId)
          }
        }
      } catch (err) {
        // Silent fail for network errors - don't interrupt user experience
        setError('Audio playback failed')
      } finally {
        setIsPlaying(false)
      }
    },
    [audio_pronunciation_setting, playAudio, stopCurrentAudio],
  )

  return {
    playWordAudio,
    stopCurrentAudio,
    isPlaying,
    error,
    hasAudioRecording,
  }
}
